import React, { useState } from 'react'
import { Modal } from '../../../shared/components/ui/Modal'
import { Button } from '../../../shared/components/ui/Button'
import type { Sprint } from '../api'

export interface CompleteSprintModalProps {
  sprint: Sprint | null
  completedCount: number
  openCount: number
  open: boolean
  onClose: () => void
  onConfirm: (moveToSprintId: string | 'backlog') => Promise<void> | void
  otherSprints: Sprint[]
}

export function CompleteSprintModal({
  sprint,
  completedCount,
  openCount,
  open,
  onClose,
  onConfirm,
  otherSprints,
}: CompleteSprintModalProps) {
  const [destination, setDestination] = useState<string>('backlog')
  const [busy, setBusy] = useState(false)

  if (!sprint) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      await onConfirm(destination)
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Complete ${sprint.name}`} size="md">
      <form onSubmit={handleSubmit} className="space-y-4 p-5">
        <div className="rounded-lg bg-gray-50 dark:bg-slate-800/60 p-3.5 space-y-1.5 text-xs">
          <div className="flex justify-between items-center text-gray-700 dark:text-slate-300">
            <span>Completed issues:</span>
            <span className="font-semibold text-emerald-600 dark:text-emerald-400">{completedCount}</span>
          </div>
          <div className="flex justify-between items-center text-gray-700 dark:text-slate-300">
            <span>Open / incomplete issues:</span>
            <span className="font-semibold text-amber-600 dark:text-amber-400">{openCount}</span>
          </div>
        </div>

        {openCount > 0 && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1.5">
              Move open issues to
            </label>
            <select
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            >
              <option value="backlog">Backlog</option>
              {otherSprints
                .filter((s) => s.id !== sprint.id && s.status !== 'completed')
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-slate-800">
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={busy}>
            Complete Sprint
          </Button>
        </div>
      </form>
    </Modal>
  )
}
